import styles from './EInvoiceTypePicker.module.scss';

import { ChevronRight } from '@mui/icons-material';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { EInvoiceType } from '@work4all/models/lib/Enums/EInvoiceType.enum';

import { Divider } from '../../../dataDisplay/divider/Divider';

const FIRST_LEVEL_OPTIONS = [
  EInvoiceType.COMMERCIAL_INVOICE,
  EInvoiceType.PARTIAL_INVOICE,
  EInvoiceType.PARTIAL_CONSTRUCTION_INVOICE,
  EInvoiceType.FINAL_CONSTRUCTION_INVOICE,
];

const SECOND_LEVEL_OPTIONS = [EInvoiceType.CORRECTED_INVOICE];

const THIRD_LEVEL_OPTIONS = [
  EInvoiceType.CREDIT_NOTE,
  EInvoiceType.PARTIAL_FINAL_CONSTRUCTION_INVOICE,
  EInvoiceType.SELF_BILLED_INVOICE,
];

const OPTIONS_WITH_TOOLTIPS = [
  EInvoiceType.PARTIAL_CONSTRUCTION_INVOICE,
  EInvoiceType.PARTIAL_INVOICE,
];

export type EInvoiceTypePickerProps = {
  onChange: (value: EInvoiceType) => void;
};

export function EInvoiceTypePicker(props: EInvoiceTypePickerProps) {
  const { t } = useTranslation();

  const handleClick = (item: EInvoiceType) => {
    props.onChange(item);
  };

  const popoverState = usePopoverState('bottom');

  const moreButtonRef = useRef<HTMLDivElement>(null);
  return (
    <>
      <Popover
        {...popoverState}
        anchorEl={moreButtonRef.current}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <List>
          {THIRD_LEVEL_OPTIONS.map((item, idx) => (
            <EInvoiceTypePickerItem
              key={idx}
              item={item}
              handleClick={handleClick}
            />
          ))}
        </List>
      </Popover>
      <List>
        {FIRST_LEVEL_OPTIONS.map((item, idx) => (
          <EInvoiceTypePickerItem
            key={idx}
            item={item}
            handleClick={handleClick}
          />
        ))}
        <Divider className={styles.divider} />
        {SECOND_LEVEL_OPTIONS.map((item, idx) => (
          <EInvoiceTypePickerItem
            key={idx}
            item={item}
            handleClick={handleClick}
          />
        ))}
        <Divider className={styles.divider} />
        <ListItemButton
          className={styles.listItem}
          onClick={() => popoverState.handleClick()}
          ref={moreButtonRef}
        >
          <ListItemText primary={t(`COMMON.MORE`)} />
          <ListItemIcon>
            <ChevronRight />
          </ListItemIcon>
        </ListItemButton>
      </List>
    </>
  );
}

export const EInvoiceTypePickerItem = ({
  item,
  handleClick,
}: {
  item: EInvoiceType;
  handleClick: (item: EInvoiceType) => void;
}) => {
  const { t } = useTranslation();

  const tooltipHidden = !OPTIONS_WITH_TOOLTIPS.includes(item);

  return (
    <Tooltip
      title={tooltipHidden ? null : t(`INVOICE_POSITION_KINDS.TOOLTIP.${item}`)}
      placement="left"
    >
      <ListItemButton
        className={styles.listItem}
        onClick={() => handleClick(item)}
      >
        <Typography>{t(`E_INVOICE_TYPE.${item}`)}</Typography>
      </ListItemButton>
    </Tooltip>
  );
};
